import React, { useContext, useEffect, useState } from "react";
import { Form, TimePicker, message, Button, Col, Upload, UploadFile, Modal, Select, Input, Row } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { getAttendancebyID, updateAttendance } from "../../utils/API";
import { AuthContext } from "../../Auth/AuthContext";
import { BiSolidInbox } from "react-icons/bi";
import { MdDeleteOutline } from "react-icons/md";

dayjs.extend(customParseFormat);

interface EditCustomersModalProps {
  visible: boolean;
  taskId?: string;
  customerId?: string;
  onClose?: () => void;
  onEdit?: () => void;
  onCancel?: () => void;
  onSuccess?: () => void;
}

interface AttendanceFormValues {
  clockIn?: dayjs.Dayjs;
  clockOut?: dayjs.Dayjs;
  lunchStart?: dayjs.Dayjs;
  lunchEnd?: dayjs.Dayjs;
  reason?: string;
  status?: string;
}

interface AttendanceResponse {
  success: boolean;
  message: string;
  data?: any;
}

const EditAttendance: React.FC<EditCustomersModalProps> = (props) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { visible, onCancel, taskId, onClose, customerId, onSuccess } = props;
  const { Option } = Select;
  const [isLoading, setIsLoading] = useState(false);
  const { authData } = useContext(AuthContext);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [previewImage, setPreviewImage] = useState<string>("");
  const [profileFileList, setProfileFileList] = useState<UploadFile[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [initialValues, setInitialValues] = useState<any>(null);
  const [adminSelectedStatus, setAdminSelectedStatus] = useState<string | null>(null);

  useEffect(() => {
    const fetchAttendanceDetails = async () => {
      try {
        const response = await getAttendancebyID(customerId);
        if (response.success) {
          const formattedData = {
            ...response.data,
            clockIn: response.data.clockIn ? dayjs(response.data.clockIn, "HH:mm") : null,
            lunchStart: response.data.lunchStart ? dayjs(response.data.lunchStart, "HH:mm") : null,
            lunchEnd: response.data.lunchEnd ? dayjs(response.data.lunchEnd, "HH:mm") : null,
            clockOut: response.data.clockOut ? dayjs(response.data.clockOut, "HH:mm") : null,
            reason: response.data.reason || "",
            status: response.data.status || "Present",
          };

          setInitialValues(formattedData);
          form.setFieldsValue(formattedData);

          if (response.data.photo) {
            setPreviewImage(response.data.photo);
            setProfileFileList([
              {
                uid: "-1",
                name: "existing-photo",
                status: "done",
                url: response.data.photo,
              },
            ]);
          }

          if (authData?.roleId?.roleId === 1) {
            setAdminSelectedStatus(formattedData.status);
          }
        } else {
          message.error("Failed to fetch attendance details.");
        }
      } catch (error) {
        message.error("Error fetching attendance data.");
      }
    };

    if (customerId) {
      fetchAttendanceDetails();
    }
  }, [customerId, form, authData?.roleId?.roleId]);

  // Calculate status based on time fields
  const calculateStatus = (values: AttendanceFormValues): string => {
    if (!values.clockIn || !values.clockOut) {
      return "Absent";
    }

    let totalMinutes = dayjs(values.clockOut, "HH:mm").diff(dayjs(values.clockIn, "HH:mm"), "minute");

    if (values.lunchStart && values.lunchEnd) {
      const lunchDuration = dayjs(values.lunchEnd, "HH:mm").diff(dayjs(values.lunchStart, "HH:mm"), "minute");
      if (lunchDuration > 0) {
        totalMinutes -= lunchDuration;
      }
    }

    // Same rule as the backend calculateStatus
    if (totalMinutes >= 8 * 60) return "Present";
    if (totalMinutes >= 4 * 60) return "HalfDay";
    return "Absent";
  };

  // Update status when time fields change
  useEffect(() => {
    if (authData?.roleId?.roleId !== 1 || !adminSelectedStatus) {
      const values = form.getFieldsValue(["clockIn", "clockOut", "lunchStart", "lunchEnd"]);
      const calculatedStatus = calculateStatus(values);
      form.setFieldsValue({ status: calculatedStatus });
    }
  }, [form, authData?.roleId?.roleId, adminSelectedStatus]);

  const handleSubmit = async (values: AttendanceFormValues) => {
    if (!customerId) {
      message.error("Invalid attendance ID.");
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();

      if (file instanceof File) {
        formData.append("photo", file);
      } else if (previewImage && previewImage.startsWith("http")) {
        formData.append("photoUrl", previewImage);
      }

      const formatTime = (time?: dayjs.Dayjs): string => {
        if (!time) return "";
        const formatted = time.format("HH:mm");
        if (formatted === "Invalid Date") throw new Error("Invalid time format");
        return formatted;
      };

      const clockIn = formatTime(values.clockIn);
      const clockOut = formatTime(values.clockOut);
      const lunchStart = formatTime(values.lunchStart);
      const lunchEnd = formatTime(values.lunchEnd);

      if (clockIn && clockOut && dayjs(clockOut, "HH:mm").isBefore(dayjs(clockIn, "HH:mm"))) {
        throw new Error("Clock out time cannot be before clock in time");
      }

      if (lunchStart && lunchEnd && dayjs(lunchEnd, "HH:mm").isBefore(dayjs(lunchStart, "HH:mm"))) {
        throw new Error("Lunch end time cannot be before lunch start time");
      }

      formData.append("clockIn", clockIn);
      formData.append("clockOut", clockOut);
      formData.append("lunchStart", lunchStart);
      formData.append("lunchEnd", lunchEnd);

      formData.append("reason", values.reason || "");
      formData.append("roleId", authData?.roleId?.roleId?.toString() || "");

      // Use admin-selected status if available, otherwise use calculated status
      const status = authData?.roleId?.roleId === 1 && adminSelectedStatus ? adminSelectedStatus : calculateStatus(values);
      formData.append("status", status);

      const calculateWorkingHours = (): string => {
        if (!clockIn || !clockOut) return "0h 0m";

        let totalMinutes = dayjs(clockOut, "HH:mm").diff(dayjs(clockIn, "HH:mm"), "minute");

        if (lunchStart && lunchEnd) {
          const lunchDuration = dayjs(lunchEnd, "HH:mm").diff(dayjs(lunchStart, "HH:mm"), "minute");
          if (lunchDuration > 0) {
            totalMinutes -= lunchDuration;
          }
        }

        return totalMinutes > 0 ? `${Math.floor(totalMinutes / 60)}h ${totalMinutes % 60}m` : "0h 0m";
      };

      formData.append("endTime", calculateWorkingHours());

      const response: AttendanceResponse = await updateAttendance(customerId, formData);

      if (!response.success) {
        throw new Error(response.message || "Failed to update attendance");
      }

      const successMessages: Record<string, () => void> = {
        "Attendance updated successfully": () => {
          message.success("Attendance updated successfully!");
          onSuccess?.();
        },
        "Attendance edit request submitted for approval.": () => {
          message.info("Attendance edit request submitted. Waiting for admin approval.");
          onSuccess?.();
        },
      };

      const successHandler =
        response.message in successMessages
          ? successMessages[response.message as keyof typeof successMessages]
          : () => message.success("Attendance record processed");

      successHandler();
      onClose?.();
    } catch (err: unknown) {
      console.error("Attendance update error:", err);
      const errorMessage = err instanceof Error ? err.message : "Error updating attendance";
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getBase64 = (file: File | Blob) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });

  const handleRemove = () => {
    setPreviewImage("");
    setProfileFileList([]);
    setFile(null);
  };

  const imageProps = {
    name: "screenshot",
    multiple: false,
    beforeUpload(file: File) {
      const isImage = file.type.startsWith("image/");
      if (!isImage) {
        message.error("You can only upload image files!");
        return Upload.LIST_IGNORE;
      }
      const isLt64M = file.size / 1024 / 1024 < 64;
      if (!isLt64M) {
        message.error("Image must be smaller than 64MB!");
        return Upload.LIST_IGNORE;
      }

      setFile(file);
      return false;
    },
    onChange(info: any) {
      const newFileList = [...info.fileList];
      if (newFileList.length > 0) {
        const file = newFileList[0];
        if (!file.url && !file.preview) {
          getBase64(file.originFileObj).then((preview) => {
            file.preview = preview;
            setPreviewImage(preview);
          });
        } else {
          setPreviewImage(file.url || file.preview || "");
        }
      } else {
        setPreviewImage("");
      }
      setProfileFileList(newFileList.slice(-1));
    },
    fileList: profileFileList,
    onRemove: handleRemove,
  };

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={600}
      centered={false}
      destroyOnClose
      className="custom-modal"
      bodyStyle={{
        overflowY: "auto",
        fontFamily: "inter, sans-serif",
      }}
      style={{
        bottom: 20,
        top: 20,
        // maxHeight: 'calc(100vh - 30px)',
        margin: '0 auto',
        position: 'relative',
      }}
      styles={{
        header: {
          padding: "12px",
          position: "sticky",
          top: 0,
          backgroundColor: "#f1f5ffff",
        },
        content: {
          padding: 0,
          overflow: "hidden",
        },
      }}
      title={
        <div
          style={{
            color: "#4b5563",
            fontFamily: "inter, sans-serif",
            fontSize: "20px",
            fontWeight: 500,
          }}
        >
          Edit Attendance
        </div>
      }
    >
      <Form
        onFinish={handleSubmit}
        onValuesChange={(_, all) => {
          if (authData?.roleId?.roleId !== 1) form.setFieldsValue({ status: calculateStatus(all) });
        }}
        form={form}
        layout="horizontal"
        labelCol={{ span: 5 }}
        wrapperCol={{ span: 19 }}
        colon={false}
        initialValues={initialValues}
        style={{ margin: 0 }}
      >
        <div className="elements">
          {/* Clock In */}
          <Col xs={24}>
            <Form.Item
              name="clockIn"
              required={false}
              rules={[{ required: true, message: "Please select Clock In Time" }]}
              label={
                <span style={{
                  color: '#4b5563',
                  fontFamily: 'inter, sans-serif',
                  fontSize: '14px',
                  paddingLeft: "15px",
                  fontWeight: 500,
                }}>
                  Clock In <span className="text-danger">*</span>
                </span>
              }
            >
              <TimePicker use12Hours format="hh:mm A" className="Allinput" needConfirm={false} />
            </Form.Item>
          </Col>

          {/* Lunch Start */}
          <Col xs={24}>
            <Form.Item
              name="lunchStart"
              required={false}
              rules={[{ required: true, message: "Please enter Lunch Start Time" }]}
              label={
                <span style={{
                  color: '#4b5563',
                  fontFamily: 'inter, sans-serif',
                  fontSize: '14px',
                  paddingLeft: "15px",
                  fontWeight: 500,
                }}>
                  Lunch Start <span className="text-danger">*</span>
                </span>
              }
            >
              <TimePicker use12Hours format="hh:mm A" className="Allinput" needConfirm={false} />
            </Form.Item>
          </Col>

          {/* Lunch End */}
          <Col xs={24}>
            <Form.Item
              name="lunchEnd"
              required={false}
              rules={[{ required: true, message: "Please enter Lunch End Time" }]}
              label={
                <span style={{
                  color: '#4b5563',
                  fontFamily: 'inter, sans-serif',
                  fontSize: '14px',
                  paddingLeft: "15px",
                  fontWeight: 500,
                }}>
                  Lunch End <span className="text-danger">*</span>
                </span>
              }
            >
              <TimePicker use12Hours format="hh:mm A" className="Allinput" needConfirm={false} />
            </Form.Item>
          </Col>

          {/* Clock Out */}
          <Col xs={24}>
            <Form.Item
              name="clockOut"
              required={false}
              rules={[{ required: true, message: "Please enter Clock Out Time" }]}
              label={
                <span style={{
                  color: '#4b5563',
                  fontFamily: 'inter, sans-serif',
                  fontSize: '14px',
                  paddingLeft: "15px",
                  fontWeight: 500,
                }}>
                  Clock Out <span className="text-danger">*</span>
                </span>
              }
            >
              <TimePicker use12Hours format="hh:mm A" className="Allinput" needConfirm={false} />
            </Form.Item>
          </Col>

          {/* Status Dropdown */}
          <Col xs={24}>
            <Form.Item
              name="status"
              label={<span style={{
                color: '#4b5563',
                fontFamily: 'inter, sans-serif',
                fontSize: '14px',
                paddingLeft: "15px",
                fontWeight: 500,
              }}>Status</span>}
            >
              <Select
                className="CustomSelect"
                disabled={authData?.roleId?.roleId !== 1}
                onChange={(value) => {
                  if (authData?.roleId?.roleId === 1) {
                    setAdminSelectedStatus(value);
                  }
                }}
              >
                <Option value="Present">Present</Option>
                <Option value="Absent">Absent</Option>
                <Option value="HalfDay">HalfDay</Option>
                <Option value="Leave">Leave</Option>
                <Option value="Holiday">Holiday</Option>
                <Option value="WeekOff">WeekOff</Option>
                <Option value="HalfLeave">HalfLeave</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24}>

            {/* Reason Field */}
            {authData?.roleId?.roleId !== 1 && (
              <Form.Item
                label={
                  <span style={{
                    color: '#4b5563',
                    fontFamily: 'inter, sans-serif',
                    fontSize: '14px',
                    paddingLeft: "15px",
                    fontWeight: 500,
                  }}>
                    Reason <span className="text-red-500">*</span>
                  </span>
                }
                name="reason"
                required={false}
                rules={[{ required: true, message: "Please provide a reason" }]}
                labelCol={{ span: 5 }}
                wrapperCol={{ span: 19 }}
              >
                <Input.TextArea
                  className="Allinputtextarea"
                  placeholder="Enter reason for miss punch"
                  rows={4}
                />
              </Form.Item>
            )}
          </Col>

          {/* Screenshot Upload */}
          {authData?.roleId?.roleId !== 1 && (
            <Col xs={24}>
              <Form.Item
                name="photo"
                label={
                  <span style={{
                    color: '#4b5563',
                    fontFamily: 'inter, sans-serif',
                    fontSize: '14px',
                    paddingLeft: "15px",
                    fontWeight: 500,
                  }}>Screenshot</span>}
                rules={[{ required: false, message: "Please upload Screenshot" }]}
              >
                <div className="h-36">
                  <Upload.Dragger {...imageProps}>
                    {!previewImage ? (
                      <div>
                        <p className="ant-upload-drag-icon d-flex justify-center">
                          <BiSolidInbox />
                        </p>
                        <p className="ant-upload-text">
                          Click or drag file to this area to upload
                        </p>
                      </div>
                    ) : (
                      <div className="relative h-40 w-40">
                        <img src={previewImage} alt="Uploaded preview" className="w-full h-full" />
                        <Button
                          className="absolute top-0 right-0 bg-orange-500 text-black p-1"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemove();
                          }}
                        >
                          <MdDeleteOutline />
                        </Button>
                      </div>
                    )}
                  </Upload.Dragger>
                </div>
              </Form.Item>
            </Col>
          )}
        </div>


        {/* Submit Button */}
        <Row justify="end">
          <Col xs={24} sm={24} md={24} xl={24} xxl={20} className="d-flex justify-content-end me-3">
            <div className="button-wrapper">
              <button
                className="allcanclebutton"
                onClick={onClose}
                type="button"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                className="allsubmitebutton w-44"
                type="submit"
                disabled={isLoading}
              >
                {isLoading ? "Updating..." : "Update"}
              </button>
            </div>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default EditAttendance;